import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import LandingHeader from "../../Components/Landing/LandingHeader";
import LandingFooter from "../../Components/Landing/LandingFooter";
import SuccessIcon from "../../assets/img/success.svg";
import FailedIcon from "../../assets/img/failed.svg";
import PayCard from "./PayCard";
import tokenService from "../../Services/token.service";
import CartService from "../../Services/CartService";
import PhotoBook5 from "../../assets/img/PhotoBook-8x8.jpg";
import shippingStore from "../../stores/ShippingStore";


interface OrderSummary { subTotal: number; discount: number; taxAmount: number; totalAmount: number; deliveryCharges: number; }
interface CartItem {
  cartID: number; userId: number; associatedProductID: number; productTypeID: number;
  productName: string; quantity: number; price: number; productSize: string;
  productID: number; productImageUrl: string;
}

const C = {
  page: '#EEEEEE', surface: '#FFFFFF', surface2: '#FFFFFF', surface3: '#F3F7FF', surface4: '#EAF1FF',
  border: '#E5E7EB', divider: '#F1F5F9',
  text1: '#0B1B3F', text2: '#475569', text3: '#64748B',
  accent: '#0B5FFF', accentSoft: '#EAF1FF', accentBorder: 'rgba(11,95,255,0.3)',
  success: '#0B5FFF', successHover: '#0847C7', danger: '#DC2626',
};

function PaymentDetails() {
  const [userEmail, setUserEmail]         = useState("");
  const [userId, setUserId]               = useState("");
  const [orderSummary, setOrderSummary]   = useState<OrderSummary | null>(null);
  const [cartItems, setCartItems]         = useState<CartItem[]>([]);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [shippingDetails, setShippingDetails] = useState<any>(null);

  useEffect(() => {
    setUserEmail(tokenService.getUser().user.email);
    setUserId(tokenService.getUser().user.id);
    const existing = shippingStore.getShippingDetails();
    if (existing) setShippingDetails(existing);
  }, []);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const { cartItems, subtotal, deliveryCharge, taxAmount, totalAmount } = await CartService.GetCart();
        setCartItems(cartItems);
        setOrderSummary({ subTotal: subtotal, discount: 3, taxAmount, totalAmount, deliveryCharges: deliveryCharge });
      } catch { /* silent */ }
    };
    const fetchCount = async () => {
      try { const { CartCount } = await CartService.GetCartCount(); setCartItemCount(CartCount); } catch { /* silent */ }
    };
    fetchCart(); fetchCount();
  }, []);

  const sHead = (text: string) => (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" as const, color: C.text3, padding: "11px 16px", borderBottom: `1px solid ${C.divider}` }}>
      {text}
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col font-jakarta text-ink antialiased" style={{ background: C.page }}>
      <LandingHeader cartItemsCount={cartItemCount} />

      {/* Step progress */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 0" }}>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {[
            { step: 1, label: "Your Cart",        done: true,  active: false },
            { step: 2, label: "Shipping Details", done: true,  active: false },
            { step: 3, label: "Payment Details",  done: false, active: true  },
          ].map((s, i, arr) => (
            <React.Fragment key={s.step}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: s.done ? C.success : s.active ? C.accent : C.surface3,
                  border: `2px solid ${s.done ? C.success : s.active ? C.accent : C.border}`,
                  color: (s.done || s.active) ? "#fff" : C.text3,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700,
                }}>
                  {s.done ? "✓" : s.step}
                </div>
                <span style={{ fontSize: 11, fontWeight: s.active ? 600 : 400, color: s.active ? C.text1 : s.done ? C.success : C.text3, whiteSpace: "nowrap" }}>
                  {s.label}
                </span>
              </div>
              {i < arr.length - 1 && <div style={{ width: 80, height: 1, background: C.border, margin: "0 8px", marginBottom: 16 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, width: "100%", maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
        {/* Back + title */}
        <div style={{ marginBottom: 28 }}>
          <Link to="/shippingdetails" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.text3, fontSize: 13, textDecoration: "none", marginBottom: 16 }}
            onMouseEnter={e => (e.currentTarget.style.color = C.text1)}
            onMouseLeave={e => (e.currentTarget.style.color = C.text3)}
          >
            <ChevronLeftIcon style={{ width: 16, height: 16 }} /> Edit Shipping Details
          </Link>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.text1, margin: 0 }}>Payment Details</h1>
        </div>

        <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>

          {/* Left */}
          <div style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Cart items */}
            <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              {sHead("Cart Details")}
              <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, maxHeight: 280, overflowY: "auto" }}>
                {cartItems.length === 0 ? (
                  <p style={{ color: C.text3, fontSize: 13 }}>Your cart is empty.</p>
                ) : (
                  cartItems.map(item => (
                    <div key={item.cartID} style={{
                      display: "flex", alignItems: "center", gap: 14,
                      padding: "12px", borderRadius: 10,
                      background: C.surface3, border: `1px solid ${C.border}`,
                    }}>
                      <div style={{ width: 80, height: 56, borderRadius: 6, overflow: "hidden", flexShrink: 0, background: C.surface4, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {/* Photobooks have no saved preview, so the URL can be empty or unreachable — fall back to the stock image */}
                        <img src={item.productImageUrl || PhotoBook5} alt={item.productName}
                          onError={e => { if (!e.currentTarget.src.endsWith(PhotoBook5)) e.currentTarget.src = PhotoBook5; }}
                          style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.productName}</div>
                        <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>Size: {item.productSize}</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text1, flexShrink: 0 }}>${item.price}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Shipping summary */}
            <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              {sHead("Shipping Details")}
              <div style={{ padding: 20 }}>
                {shippingDetails ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
                    {[
                      { label: "Name",         value: `${shippingDetails.firstName || ""} ${shippingDetails.lastName || ""}`.trim() || "N/A" },
                      { label: "Phone",        value: shippingDetails.phoneNo || "N/A" },
                      { label: "Address",      value: shippingDetails.shippingAddress || "N/A" },
                      { label: "City",         value: shippingDetails.city || "N/A" },
                      { label: "State",        value: shippingDetails.state || "N/A" },
                      { label: "Country",      value: shippingDetails.country || "N/A" },
                      { label: "Postal Code",  value: shippingDetails.zipCode || "N/A" },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" as const, color: C.text3 }}>{label}</span>
                        <span style={{ fontSize: 13, color: C.text1 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: C.text3, fontSize: 13 }}>No shipping details found.</p>
                )}
              </div>
            </div>

            {/* PayCard widget */}
            <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
              {sHead("Payment")}
              <div style={{ padding: 20 }}>
                <PayCard userEmail={userEmail} userId={userId} paymentAmount={orderSummary?.totalAmount} />
              </div>
            </div>
          </div>

          {/* Right: Order Summary */}
          <div style={{ width: 320, flexShrink: 0 }}>
            <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", position: "sticky" as const, top: 24 }}>
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.divider}`, background: C.surface, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: C.accentSoft, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                    <rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12h6M9 16h4"/>
                  </svg>
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.text1 }}>Order Summary</span>
              </div>
              <div style={{ padding: "16px 18px" }}>
                {[
                  { label: "Total Items",      value: String(cartItems.length) },
                  { label: "Sub Total",        value: `$${orderSummary?.subTotal?.toFixed(2) ?? "0.00"}` },
                  { label: "Delivery Charges", value: `$${orderSummary?.deliveryCharges?.toFixed(2) ?? "0.00"}` },
                  { label: "Tax",              value: `$${orderSummary?.taxAmount?.toFixed(2) ?? "0.00"}` },
                ].map(row => (
                  <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${C.divider}` }}>
                    <span style={{ fontSize: 13, color: C.text2 }}>{row.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.text1 }}>{row.value}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 0 4px" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text1 }}>Total</span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: C.accent, letterSpacing: -0.5 }}>
                    ${orderSummary?.totalAmount?.toFixed(2) ?? "0.00"}
                  </span>
                </div>
              </div>
              <div style={{ padding: "0 18px 18px" }}>
                <button
                  className="ipayfield" data-ipayname="submit"
                  style={{
                    width: "100%", height: 44,
                    background: C.success, border: "none", borderRadius: 999,
                    color: "#fff", fontSize: 13, fontWeight: 700,
                    letterSpacing: 0.5, textTransform: "uppercase" as const,
                    cursor: "pointer", transition: "all 150ms",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = C.successHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = C.success)}
                >
                  Confirm Order
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Payment success/failed (controlled by IntelliPay SDK via class toggle) */}
        <section className="PaymentSuccess hidden" style={{ marginTop: 32 }}>
          <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface2, padding: "48px 24px", textAlign: "center" }}>
            <img src={SuccessIcon} alt="Success" style={{ width: 64, height: 64, margin: "0 auto 16px" }} />
            <h3 style={{ fontSize: 22, fontWeight: 700, color: C.text1, marginBottom: 8 }}>Payment Successful!</h3>
            <p style={{ color: C.text2, marginBottom: 4 }}>Your payment has been processed!</p>
            <p style={{ color: C.text3, fontSize: 13, marginBottom: 24 }}>Details of the transaction have been included below.</p>
            <Link to="/">
              <button style={{ padding: "10px 32px", background: C.success, border: "none", borderRadius: 999, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", textTransform: "uppercase" as const }}>
                Home
              </button>
            </Link>
          </div>
        </section>

        <section className="PaymentFailed hidden" style={{ marginTop: 32 }}>
          <div style={{ borderRadius: 14, border: `1px solid rgba(220,38,38,0.25)`, background: C.surface2, padding: "48px 24px", textAlign: "center" }}>
            <img src={FailedIcon} alt="Failed" style={{ width: 64, height: 64, margin: "0 auto 16px" }} />
            <h3 style={{ fontSize: 22, fontWeight: 700, color: C.danger, marginBottom: 8 }}>Payment Failed</h3>
            <p style={{ color: C.text2, marginBottom: 4 }}>Unable to process your payment. Please try again.</p>
            <p style={{ color: C.text3, fontSize: 13, marginBottom: 24 }}>This payment has been declined by your card issuer. Error code: CSC_7200015</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <Link to="/">
                <button style={{ padding: "10px 24px", background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Try Again
                </button>
              </Link>
              <Link to="/">
                <button style={{ padding: "10px 24px", background: C.success, border: "none", borderRadius: 999, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", textTransform: "uppercase" as const }}>
                  Home
                </button>
              </Link>
            </div>
          </div>
        </section>
      </div>
      <LandingFooter />
    </div>
  );
}

export default PaymentDetails;
